import type { ReviveOfferResponse } from "../api/dto/player.dto.js";

export type LevelDifficultyName = "Common" | "Hard" | "SuperHard";
export const LEVEL_DIFFICULTY_NAMES: readonly LevelDifficultyName[] = [ "Common", "Hard", "SuperHard" ];

export interface EconomyConfig
{
  max_hearts: number;
  heart_regen_minutes: number;
  coins_per_star: number;
  continue_balls_price: number;
  fireworks_price: number;
  fireworks_pack_qty: number;
  heart_refill_price: number;
}

export const ECONOMY_DEFAULTS: EconomyConfig = {
  max_hearts: 3,
  heart_regen_minutes: 20,
  coins_per_star: 40,
  continue_balls_price: 1500,
  fireworks_price: 1500,
  fireworks_pack_qty: 3,
  heart_refill_price: 1500,
};

function isObject( value: unknown ): value is Record<string, unknown>
{
  return !!value && typeof value === "object" && !Array.isArray( value );
}

function isNonNegativeInteger( value: unknown ): value is number
{
  return typeof value === "number" && Number.isInteger( value ) && value >= 0;
}

function isPositiveInteger( value: unknown ): value is number
{
  return typeof value === "number" && Number.isInteger( value ) && value > 0;
}

interface RewardRule
{
  universal: number | null;
  by_difficulty: Partial<Record<LevelDifficultyName, number>>;
}

function parseRewardRule( value: unknown ): RewardRule | null
{
  if ( !isObject( value ) ) return null;
  const universal = isNonNegativeInteger( value.coins ) ? value.coins : null;
  const by_difficulty: Partial<Record<LevelDifficultyName, number>> = {};
  if ( isObject( value.rewards ) )
  {
    for ( const difficulty of LEVEL_DIFFICULTY_NAMES )
    {
      const entry = value.rewards[difficulty];
      if ( isObject( entry ) && isNonNegativeInteger( entry.coins ) )
        by_difficulty[difficulty] = entry.coins;
    }
  }
  return universal !== null || Object.keys( by_difficulty ).length > 0
    ? { universal, by_difficulty }
    : null;
}

function rewardForDifficulty( rule: RewardRule | null | undefined, difficulty: LevelDifficultyName ): number | null
{
  if ( !rule ) return null;
  return rule.by_difficulty[difficulty] ?? rule.universal ?? null;
}

export function resolveLevelRewardCoins(
  raw: unknown,
  level: number,
  difficulty: LevelDifficultyName,
  fallback_coins: number
): number
{
  if ( !isObject( raw ) ) return fallback_coins;

  if ( Array.isArray( raw.levels ) )
  {
    for ( const entry of raw.levels )
    {
      if ( !isObject( entry ) || entry.level !== level ) continue;
      const coins = rewardForDifficulty( parseRewardRule( entry ), difficulty );
      if ( coins !== null ) return coins;
    }
  }

  if ( Array.isArray( raw.ranges ) )
  {
    for ( const entry of raw.ranges )
    {
      if ( !isObject( entry ) || !isNonNegativeInteger( entry.min_level ) ) continue;
      if ( entry.max_level !== undefined && ( !isPositiveInteger( entry.max_level ) || entry.max_level < entry.min_level ) ) continue;
      if ( level < entry.min_level || ( entry.max_level !== undefined && level > entry.max_level ) ) continue;
      const rule = parseRewardRule( entry );
      if ( !rule ) continue;
      const coins = rewardForDifficulty( rule, difficulty );
      if ( coins !== null ) return coins;
      break;
    }
  }

  return rewardForDifficulty( parseRewardRule( raw.default ), difficulty ) ?? fallback_coins;
}

interface ReviveSchedule
{
  max_purchases: number;
  offers: { price: number; shots: number }[];
}

function parseReviveSchedule( value: unknown, fallback: ReviveSchedule | null ): ReviveSchedule | null
{
  if ( !isObject( value ) || !isNonNegativeInteger( value.max_purchases ) || !Array.isArray( value.offers ) )
    return fallback;
  const offers: { price: number; shots: number }[] = [];
  for ( const raw of value.offers )
  {
    if ( isObject( raw ) && isNonNegativeInteger( raw.price ) && isPositiveInteger( raw.shots ) )
      offers.push( { price: raw.price, shots: raw.shots } );
  }
  if ( offers.length < value.max_purchases ) return fallback;
  return { max_purchases: value.max_purchases, offers };
}

export function resolveReviveOfferFromConfig(
  raw: unknown,
  level: number,
  purchase_index: number,
  legacy_price: number
): ReviveOfferResponse
{
  const fallback_schedule: ReviveSchedule = { max_purchases: 1, offers: [ { price: legacy_price, shots: 5 } ] };
  let min_level = 1;
  let schedule: ReviveSchedule = fallback_schedule;

  if ( isObject( raw ) )
  {
    min_level = isPositiveInteger( raw.min_level ) ? raw.min_level : 1;
    const default_schedule = parseReviveSchedule( raw.default, fallback_schedule ) ?? fallback_schedule;
    schedule = default_schedule;
    if ( Array.isArray( raw.ranges ) )
    {
      for ( const entry of raw.ranges )
      {
        if ( !isObject( entry ) || !isPositiveInteger( entry.min_level ) ) continue;
        if ( entry.max_level !== undefined && ( !isPositiveInteger( entry.max_level ) || entry.max_level < entry.min_level ) ) continue;
        const range = parseReviveSchedule( entry, null );
        if ( !range ) continue;
        if ( level >= entry.min_level && ( entry.max_level === undefined || level <= entry.max_level ) )
        {
          schedule = range;
          break;
        }
      }
    }
  }

  const available = level >= min_level && purchase_index < schedule.max_purchases;
  return {
    available,
    level,
    purchase_index,
    max_purchases: schedule.max_purchases,
    remaining_purchases: Math.max( 0, schedule.max_purchases - purchase_index ),
    offer: available ? schedule.offers[purchase_index] ?? null : null,
  };
}

export function difficultyName( difficulty: number | null | undefined ): LevelDifficultyName
{
  return LEVEL_DIFFICULTY_NAMES[ typeof difficulty === "number" ? difficulty : 0 ] ?? "Common";
}
